import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, UserPlus, KeyRound, Plus, Trash2, ArrowUp, ArrowDown, Eye, Link as LinkIcon } from 'lucide-react';
import { getPortalIcon, LINK_ICON_OPTIONS } from '../portal/portalIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => (s ? new Date(s).toLocaleString('pt-BR') : 'Nunca');

export const ClientPortal = () => {
  const { selectedProject } = useApp();
  const projectId = selectedProject?.id;

  if (!projectId) {
    return <div className="p-6 text-muted-foreground" data-testid="client-portal-no-project">Selecione um projeto para configurar o portal do cliente.</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="client-portal-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Portal do cliente</h1>
        <p className="text-sm text-muted-foreground">Gerencie o acesso e o menu que seus clientes verão no portal.</p>
      </div>
      <Tabs defaultValue="usuarios">
        <TabsList>
          <TabsTrigger value="usuarios" data-testid="tab-portal-usuarios">Usuários</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-portal-menu">Menu do portal</TabsTrigger>
        </TabsList>
        <TabsContent value="usuarios" className="pt-4"><UsersTab projectId={projectId} /></TabsContent>
        <TabsContent value="menu" className="pt-4"><MenuTab projectId={projectId} /></TabsContent>
      </Tabs>
    </div>
  );
};

// ---------------- Users tab ----------------
const UsersTab = ({ projectId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/portal-admin/users?project_id=${projectId}`);
      setUsers(data);
    } catch (e) { toast.error('Erro ao carregar usuários.'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/portal-admin/users?project_id=${projectId}`, form);
      toast.success('Usuário criado. Senha temporária definida.');
      setCreateOpen(false); setForm({ name: '', email: '', password: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar usuário.'); }
    finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try {
      await axios.patch(`${API_URL}/api/portal-admin/users/${u.id}`, { is_active: !u.is_active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_active: !x.is_active } : x)));
    } catch (e) { toast.error('Erro ao atualizar.'); }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/portal-admin/users/${resetUser.id}/reset-password`, { password: resetPwd });
      toast.success('Senha redefinida. O cliente deverá trocá-la no próximo acesso.');
      setResetUser(null); setResetPwd(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao redefinir senha.'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="portal-add-user-btn"><UserPlus className="h-4 w-4 mr-2" />Novo usuário</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo usuário do portal</DialogTitle>
              <DialogDescription>O cliente deverá trocar a senha no primeiro acesso.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="portal-user-name" /></div>
              <div className="space-y-1"><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="portal-user-email" /></div>
              <div className="space-y-1"><Label>Senha temporária</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="portal-user-password" /></div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={busy || !form.name || !form.email || !form.password} data-testid="portal-user-save">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Criar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {users.length === 0 && <p className="text-sm text-muted-foreground">Nenhum usuário cadastrado.</p>}
      <div className="space-y-2">
        {users.map((u) => (
          <Card key={u.id} data-testid={`portal-user-${u.id}`}>
            <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{u.name}</span>
                  {u.is_active ? <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">Ativo</Badge>
                    : <Badge variant="outline" className="bg-slate-100 text-slate-600">Inativo</Badge>}
                  {u.must_change_password && <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">Senha temporária</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{u.email} · Último acesso: {fmtDate(u.last_login_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => { setResetUser(u); setResetPwd(''); }} data-testid={`portal-reset-${u.id}`}>
                  <KeyRound className="h-4 w-4 mr-1" />Redefinir senha
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Ativo</span>
                  <Switch checked={u.is_active} onCheckedChange={() => toggle(u)} data-testid={`portal-toggle-${u.id}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>Defina uma nova senha temporária para {resetUser?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1"><Label>Nova senha temporária</Label><Input value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="portal-reset-password-input" /></div>
          <DialogFooter>
            <Button onClick={doReset} disabled={busy || !resetPwd} data-testid="portal-reset-confirm">Redefinir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------- Menu tab ----------------
const MenuTab = ({ projectId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState({ label: '', url: 'https://', icon: 'Globe' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/portal-admin/settings?project_id=${projectId}`);
      setSettings(data);
    } catch (e) { toast.error('Erro ao carregar o menu.'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const save = async (enabled, items) => {
    setSaving(true);
    try {
      const normalized = items.map((it, i) => ({ ...it, order: i }));
      const { data } = await axios.put(`${API_URL}/api/portal-admin/settings?project_id=${projectId}`, { enabled, menu_items: normalized });
      setSettings(data);
    } catch (e) { toast.error('Erro ao salvar.'); await load(); }
    finally { setSaving(false); }
  };

  const updateItem = (id, patch) => {
    const items = settings.menu_items.map((it) => (it.id === id ? { ...it, ...patch } : it));
    setSettings({ ...settings, menu_items: items });
    return items;
  };

  const move = (idx, dir) => {
    const items = [...settings.menu_items];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    save(settings.enabled, items);
  };

  const addLink = () => {
    const item = { id: `new-${Date.now()}`, type: 'link_externo', key: null, label: link.label.trim(), url: link.url.trim(), icon: link.icon, enabled: true, order: settings.menu_items.length };
    save(settings.enabled, [...settings.menu_items, item]);
    setLinkOpen(false); setLink({ label: '', url: 'https://', icon: 'Globe' });
  };

  const removeItem = (id) => save(settings.enabled, settings.menu_items.filter((i) => i.id !== id));

  const previewAsClient = () => window.open(`/portal?preview=${projectId}`, '_blank', 'noopener');

  if (loading || !settings) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-medium">Portal ativo</p>
            <p className="text-xs text-muted-foreground">Quando desligado, os clientes não conseguem acessar o portal deste projeto.</p>
          </div>
          <div className="flex items-center gap-4">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch checked={settings.enabled} onCheckedChange={(v) => save(v, settings.menu_items)} data-testid="portal-enabled-switch" />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-sm">Itens do menu</h3>
        <div className="flex gap-2">
          <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="portal-add-link-btn"><Plus className="h-4 w-4 mr-1" />Adicionar link externo</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Adicionar link externo</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Nome</Label><Input value={link.label} onChange={(e) => setLink({ ...link, label: e.target.value })} data-testid="portal-link-label" /></div>
                <div className="space-y-1"><Label>URL</Label><Input value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} placeholder="https://" data-testid="portal-link-url" /></div>
                <div className="space-y-1">
                  <Label>Ícone</Label>
                  <Select value={link.icon} onValueChange={(v) => setLink({ ...link, icon: v })}>
                    <SelectTrigger data-testid="portal-link-icon"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LINK_ICON_OPTIONS.map((name) => {
                        const Ic = getPortalIcon(name);
                        return <SelectItem key={name} value={name}><span className="flex items-center gap-2"><Ic size={15} />{name}</span></SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addLink} disabled={!link.label.trim() || !/^https?:\/\/.+/.test(link.url.trim())} data-testid="portal-link-save">Adicionar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button size="sm" onClick={previewAsClient} data-testid="portal-preview-btn"><Eye className="h-4 w-4 mr-1" />Visualizar como cliente</Button>
        </div>
      </div>

      <div className="space-y-2">
        {settings.menu_items.map((it, idx) => {
          const Icon = getPortalIcon(it.icon);
          return (
            <Card key={it.id} data-testid={`portal-menu-item-${it.id}`}>
              <CardContent className="py-3 flex items-center gap-3 flex-wrap">
                <div className="flex flex-col">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`portal-up-${it.id}`}><ArrowUp size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => move(idx, 1)} disabled={idx === settings.menu_items.length - 1} data-testid={`portal-down-${it.id}`}><ArrowDown size={14} /></Button>
                </div>
                <Icon size={18} className="text-slate-500 shrink-0" />
                <Input
                  value={it.label}
                  onChange={(e) => updateItem(it.id, { label: e.target.value })}
                  onBlur={() => save(settings.enabled, settings.menu_items)}
                  className="max-w-[200px]"
                  data-testid={`portal-label-${it.id}`}
                />
                {it.type === 'link_externo'
                  ? <Badge variant="outline" className="gap-1"><LinkIcon size={11} />Link</Badge>
                  : <Badge variant="outline">Nativo</Badge>}
                {it.type === 'link_externo' && <span className="text-xs text-muted-foreground truncate max-w-[180px]">{it.url}</span>}
                <div className="ml-auto flex items-center gap-3">
                  <Switch checked={it.enabled} onCheckedChange={(v) => save(settings.enabled, updateItem(it.id, { enabled: v }))} data-testid={`portal-item-switch-${it.id}`} />
                  {it.type === 'link_externo' && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(it.id)} data-testid={`portal-remove-${it.id}`}><Trash2 size={15} className="text-red-500" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
